<template>
  <el-form
    ref="formRef"
    :model="ruleForm"
    :rules="rules"
    class="form-wrapper"
    @keyup.enter="onSubmit"
  >
    <el-form-item prop="countryCode">
      <CountrySelect
        :country="state.ruleForm.countryCode"
        @change="handleCountry"
        :isStar="false"
        :holder="$t('form.countryTips')"
      />
    </el-form-item>
    <el-form-item prop="account">
      <el-input
        :placeholder="$t('form.emailTips')"
        class="ipt-common"
        v-model="ruleForm.account"
        :clearable="false"
      />
    </el-form-item>
    <el-form-item prop="password">
      <el-input
        type="password"
        :placeholder="$t('form.password')"
        class="ipt-common"
        v-model="ruleForm.password"
        @change="pwdChange"
        show-password
      />
    </el-form-item>
    <el-form-item prop="confirmPassword">
      <el-input
        type="password"
        class="ipt-common"
        :placeholder="$t('form.confirmPass')"
        v-model="ruleForm.confirmPassword"
        show-password
      />
    </el-form-item>
    <el-form-item prop="code">
      <VerifyCodeInput v-model:value="ruleForm.code" ref="verifyCodeRef" />
    </el-form-item>
    <el-form-item prop="inviteCode">
      <el-input
        class="ipt-common"
        :placeholder="$t('form.inviteCodeTips')"
        v-model="ruleForm.inviteCode"
        :disabled="state.isHasCode"
        :clearable="false"
      />
    </el-form-item>
    <el-form-item prop="readPolicy">
      <div class="read-agree">
        <el-checkbox class="check" v-model="ruleForm.readPolicy" />
        {{ $t('form.readPrivacy') }}
        <span class="text text1" @click="goFaqRouter('acbuy-User-Registration-Agreement')">{{
          $t('form.termService')
        }}</span>
        <span>{{ $t('form.and') }}</span>
        <span class="text text2" @click="goFaqRouter('User-Privacy-Policy')">{{
          $t('form.privacyPolicy')
        }}</span>
      </div>
    </el-form-item>
    <el-button class="submit-button" type="primary" @click="onSubmit" :loading="state.isLoading">
      {{ $t('form.register') }}
    </el-button>
    <div class="google-tool">
      <div class="line1"></div>
      or
      <div class="line2"></div>
    </div>
    <div id="google-button" :data-width="400" :data-locale="dataLocale">
      <!-- <img class="google-img" src="@/assets/images/common/google.png" />
      {{ $t('form.continueGoogle') }} -->
    </div>
    <div class="tip-tool">
      <span class="text1"> {{ $t('form.isHaveAccount') }}</span>
      <span class="text2" @click="$emits('changeLoginStatus', 'login')">
        {{ $t('form.login') }}</span
      >
    </div>
  </el-form>
</template>

<script setup>
import { getIPAddress } from '@/apis/resource'
import { googleLogin, userRegister } from '@/apis/user'
import CountrySelect from '@/components/CountrySelect'
import { gtagPointSource } from '@/config/constant'
import { useUserStore } from '@/stores'
import { encryptPwd } from '@/utils/encrypt'
import reg from '@/utils/reg'
import { initGoogle, useGoogle } from '@/utils/useGoogleLogin'
import CryptoJS from 'crypto-js'
import { ElMessage } from 'element-plus'
import {
  computed,
  defineEmits,
  defineExpose,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  toRefs,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import VerifyCodeInput from './VerifyCodeInput.vue'

const { t } = useI18n()
const $emits = defineEmits(['changeLoginStatus'])
const verifyCodeRef = ref()

const formRef = ref()
const $router = useRouter()
const $route = useRoute()
const userStore = useUserStore()
const checkReadPolicy = (_, value, callback) => {
  if (!value) {
    // return callback(new Error('Please input the age'));
    return callback(new Error(t('form.verifyAccept')))
  }
  return callback()
}
const dataLocale = computed(() => {
  return userStore.language === 'cn' ? 'zh_CN' : userStore.language
})

const pwdChange = () => {
  const form = state.ruleForm
  if (form.password && form.confirmPassword) {
    formRef.value.validateField('confirmPassword')
  }
}
// 校验密码一致
const validateSamePsd = (_, value, callback) => {
  const form = state.ruleForm
  if (form.password && form.confirmPassword) {
    if (form.password !== form.confirmPassword) {
      callback(t('form.noSamePass'))
    }
  }
  callback()
}
const state = reactive({
  isLoading: false,
  isHasCode: false,
  ruleForm: {
    countryCode: '',
    account: '',
    password: '',
    confirmPassword: '',
    promoterCode: '',
    language: 'cn',
    code: '',
    readPolicy: false,
    inviteCode: '',
  },
  countryList: [],
  rules: {
    countryCode: [
      {
        required: true,
        trigger: 'blur',
        message: t('form.verifyEmpty'),
      },
    ],
    account: [
      {
        required: true,
        trigger: 'blur',
        message: t('form.verifyEmpty'),
      },
      {
        pattern: reg.mailReg,
        trigger: 'change',
        message: t('form.holder.inputEmailAccount'),
      },
    ],
    password: [
      {
        required: true,
        trigger: 'blur',
        message: t('form.verifyEmpty'),
      },
      {
        pattern: reg.passwordReg,
        trigger: 'blur',
        message: t('form.verifyPass'),
      },
    ],
    confirmPassword: [
      {
        required: true,
        trigger: 'blur',
        message: t('form.verifyEmpty'),
      },
      {
        pattern: reg.passwordReg,
        trigger: 'blur',
        message: t('form.verifyPass'),
      },
      { validator: validateSamePsd },
    ],
    code: [
      {
        required: true,
        trigger: 'blur',
        message: t('form.verifyEmpty'),
      },
    ],
    readPolicy: [
      {
        required: true,
        message: t('form.verifyAccept'),
        trigger: 'blur',
      },
      { validator: checkReadPolicy, trigger: 'blur' },
    ],
  },
})

const { ruleForm, rules } = toRefs(state)
// 提交回调
const onSubmit = async () => {
  if (state.isLoading) return
  state.isLoading = true
  formRef.value.validate(async (valid) => {
    if (valid) {
      const params = {
        ...ruleForm.value,
        uuid: verifyCodeRef.value.getId(),
        deviceCode: finger(),
        device: 'pc',
      }

      delete params.confirmPassword
      delete params.readPolicy
      params.password = await encryptPwd(params.password)
      const arr = [
        {
          countryCode: 'FR',
          lang: 'fr',
        },
        {
          countryCode: 'DE',
          lang: 'de',
        },
        {
          countryCode: 'IT',
          lang: 'it',
        },
      ]
      const lang = arr.find((item) => item.countryCode === ruleForm.value.countryCode)?.lang || 'en'
      try {
        const res = await userRegister(params, lang)
        registerPoint(
          {
            gtag: {
              send_to: 'G-KWYFYH5B8P',
              method: 'Email',
            },
          },
          lang,
        )
        await userStore.login(res) // 保存个人信息

        ElMessage({
          message: t('form.registerSuccess'),
          type: 'success',
        })
        const redirectUrl = $route.query.redirectUrl
          ? decodeURIComponent($route.query.redirectUrl)
          : '/register-success'

        $router.push(redirectUrl)
      } catch (error) {
        verifyCodeRef.value.getCodeImg()
        ElMessage({
          message: error,
          type: 'error',
        })
      } finally {
        state.isLoading = false
      }
    } else {
      state.isLoading = false
    }
  })
}
const handleCountry = (country) => {
  state.ruleForm.countryCode = country?.countryCode
  state.ruleForm.registerCountry = country?.countryNameCn
  formRef.value.validateField('countryCode')
}
const setIpAddress = async () => {
  const res = await getIPAddress()
  await new Promise((resolve) => setTimeout(resolve, 600))
  state.ruleForm.countryCode =
    res.countryCode === 'ZZ' ? state.ruleForm.countryCode : res.countryCode
}
const registerPoint = (params) => {
  try {
    if (process.env.NODE_ENV === 'production') {
      if ($route.query.utm_source) {
        userStore.setChannel($route.query.utm_source)
      }
      window.fbq('track', 'CompleteRegistration', params.fb)
      window.ttq.track('CompleteRegistration', params.tt)
      // 需要绑定特殊来源的渠道
      if (gtagPointSource.includes(userStore.channel)) {
        window.gtag('event', 'sign_up', params.gtag)
      }
    }
  } catch (error) {
    error
  }
}

const googleLoginCallback = async (res) => {
  const token = res.credential
  let params = {}
  if (state.ruleForm.inviteCode) {
    params = {
      token,
      deviceCode: finger(),
      device: 'pc',
      inviteCode: state.ruleForm.inviteCode,
    }
  } else {
    params = {
      token,
      deviceCode: finger(),
      device: 'pc',
    }
  }
  const res1 = await googleLogin(params)
  registerPoint({
    gtag: {
      send_to: 'G-KWYFYH5B8P',
      method: 'Google',
    },
  })
  await userStore.login(res1) // 保存个人信息
  localStorage.removeItem('redirectUrl')

  ElMessage({
    message: t('form.registerSuccess'),
    type: 'success',
  })
  $router.push({ name: 'register-success' })
}
setIpAddress()

defineExpose({ onSubmit })
function finger() {
  // 获取浏览器 User Agent 信息
  var userAgent = navigator.userAgent

  // 获取浏览器 Accept Headers 信息
  var acceptHeaders = navigator.acceptHeader

  // 获取屏幕分辨率
  var screenWidth = screen.width
  var screenHeight = screen.height

  // 获取时区偏移量
  var timezoneOffset = new Date().getTimezoneOffset()

  // 创建一个 Canvas 元素
  var canvas = document.createElement('canvas')
  var ctx = canvas.getContext('2d')

  // 绘制一个图像，并获取图像数据的哈希值
  var text = 'fingerprint'
  ctx.textBaseline = 'top'
  ctx.font = "14px 'Arial'"
  ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = '#f60'
  ctx.fillRect(125, 1, 62, 20)
  ctx.fillStyle = '#069'
  ctx.fillText(text, 2, 15)
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
  ctx.fillText(text, 4, 17)

  var canvasData = canvas.toDataURL()
  var canvasHash = hash(canvasData)

  // 获取本地 IP 地址和端口号
  var rtcPeerConnection =
    window.RTCPeerConnection || window.mozRTCPeerConnection || window.webkitRTCPeerConnection
  if (rtcPeerConnection) {
    var pc = new rtcPeerConnection({
      iceServers: [],
    })
    pc.createDataChannel('')
    pc.createOffer(
      function (result) {
        pc.setLocalDescription(
          result,
          // function () { },
          // function () { }
        )
      },
      // function () { }
    )
    pc.onicecandidate = function (event) {
      if (event && event.candidate && event.candidate.candidate) {
        // var address = event.candidate.candidate.split(' ')[4];
        // var port = event.candidate.candidate.split(' ')[5];
        // var ip = address.split(':')[0];
      }
    }
  }
  // 整合上述信息生成浏览器指纹
  var fingerprint =
    userAgent + acceptHeaders + screenWidth + screenHeight + timezoneOffset + canvasHash

  // 计算哈希值
  // eslint-disable-next-line  @typescript-eslint/no-empty-function
  function hash(str) {
    var hash = 0
    for (var i = 0; i < str.length; i++) {
      var char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash
    }
    return hash
  }
  return CryptoJS.MD5(fingerprint).toString()
}
const goFaqRouter = (code) => {
  window.open(`/doc?code=${code}`, '_blank')
}
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getQueryString = (url, name) => {
  const index = url.indexOf('?')
  if (index !== -1) {
    const params = url.slice(index + 1)
    const searchParams = new URLSearchParams(params)
    return searchParams.get(name)
  }
  return null
}
const getInviteCode = () => {
  if (document.referrer.includes('google.com') && $route.query.code) return
  const cacheRedirectUrl = [$route.query.redirectUrl, localStorage.getItem('redirectUrl')]
  let url = cacheRedirectUrl.shift()
  if (!url) url = cacheRedirectUrl.shift()
  while (url) {
    const redirectUrl = decodeURIComponent(url)
    if (!redirectUrl.includes('shareCoupon')) {
      state.ruleForm.inviteCode = getQueryString(redirectUrl, 'u')
    } else {
      state.ruleForm.inviteCode = getQueryString(redirectUrl, 'u')
      state.ruleForm.inviteRegisterSource = getQueryString(redirectUrl, 'inviteSource')
      state.ruleForm.encryptPackageNo = getQueryString(redirectUrl, 'id')
    }
    if (!state.ruleForm.inviteCode && !url.includes('/doc')) {
      state.ruleForm.inviteCode = getQueryString(url, 'code')
    }
    if (state.ruleForm.inviteCode) {
      state.isHasCode = true
      localStorage.setItem('redirectUrl', url)
      break
    }
    url = cacheRedirectUrl.shift()
  }

  if ($route.query?.code) {
    state.ruleForm.inviteCode = $route.query?.code
    state.isHasCode = true
    localStorage.setItem('redirectUrl', $route.fullPath)
  }
}
onMounted(() => {
  getInviteCode()
  const registerSource = localStorage.getItem('registerSource')
  if (registerSource) {
    state.ruleForm.inviteRegisterSource = state.ruleForm.inviteRegisterSource || registerSource
  }

  !initGoogle('google-button', googleLoginCallback) &&
    useGoogle('google-button', googleLoginCallback)
})
onUnmounted(() => {
  sessionStorage.removeItem('register-form')
})
</script>

<style lang="scss" scoped>
.form-wrapper {
  width: 400px;
  margin-top: 20px;

  .ipt-common {
    width: 400px;
    height: 44px;
  }

  .el-form-item {
    margin-bottom: 20px;
  }

  ::v-deep {
    .el-input {
      .el-input__inner {
        &::placeholder {
          font-family: Montserrat;
          font-size: 14px;
          color: #8492b2;
        }
      }

      .el-input__wrapper {
        height: 44px;
      }
    }
  }

  .read-agree {
    margin-top: -15px;
    color: var(--black-color-26);
    font-size: 14px;
    font-family: Montserrat;
    position: relative;
    width: 100%;

    .check {
      color: var(--black-color-26);
      font-weight: normal;
      top: 2px;
      margin-right: 2px;
    }

    .text {
      color: var(--primary-color);
      word-wrap: break-word;
      cursor: pointer;
    }

    .text1 {
      margin-right: 4px;
      margin-left: 4px;
    }

    .text2 {
      margin-left: 4px;
    }
  }

  .submit-button {
    cursor: pointer;
    width: 400px;
    height: 56px;
    border-radius: 8px;
    text-align: center;
    line-height: 56px;
    background-color: var(--primary-color);
    // color: var(--white-color-1);
    margin-bottom: 20px;
    margin-top: 20px;
  }

  .google-tool {
    margin-top: 30px;
    margin-bottom: 15px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: Montserrat;
    font-size: 14px;
    font-weight: 500;
    color: var(--black-color-26);

    .line1 {
      height: 1px;
      background-color: #e7e7e7;
      width: 188px;
    }

    .line2 {
      height: 1px;
      background-color: #e7e7e7;
      width: 188px;
    }
  }

  #google-button {
    font-family: Montserrat;
    font-size: 16px;
    font-weight: bold;
    color: var(--black-color-3d);
    width: 400px;
    height: 56px;
    border-radius: 8px;
    // border: 1px solid var(--border-black-1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 28px;
    // cursor: pointer;

    .google-img {
      width: 24px;
      height: 24px;
      display: block;
      margin-right: 8px;
    }
  }

  .tip-tool {
    width: 100%;
    text-align: center;
    font-family: Montserrat;
    font-size: 14px;

    .text1 {
      color: var(--black-color-9);
    }

    .text2 {
      color: var(--primary-color);
      margin-left: 4px;
      cursor: pointer;
    }
  }
}
</style>
